'use client'

import { useAtom } from 'jotai'
import { useEffect, useMemo, useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useOrderFormTradeVenue } from '@/features/order/order-form/hooks/use-order-form-trade-venue'
import { useUnifiedOrderFormContext } from '@/features/order/order-form/lib/order-form-context'
import {
  buildOptionInstrument,
  getAvailableStrikes,
  resolveOptionInstrument,
} from '@/features/order/order-form/lib/parse-option-instrument'
import { useMultiExchangeInstrumentMap } from '@/hooks/use-multi-exchange-instrument-map'
import { parseExchangeField } from '@/lib/proto/util'
import { getInstrumentMapForExchange } from '@/lib/utils/exchange-instrument-map'
import { useAccountPrimaryExchange } from '@/service/account-exchanges.hook'
import { orderFormSelectionAtomFamily } from '@/state/order'
import { SelectedOption } from '@/types/dashboard'

interface StrikePriceDropdownProps {
  /** The selected option this dropdown controls */
  option: SelectedOption
  /** Index in the selectedOptions array */
  index: number
}

/**
 * Strike price dropdown for an option in the order form.
 * Changing strike will:
 * 1. Update the selectedOptionsAtom with new instrument name
 * 2. Clear the leg's price ('-') so the form's own ticker sync fills it — never from a
 *    chart's option chain, which is another widget's (possibly different expiry/underlying).
 */
export default function StrikePriceDropdown({ option, index }: StrikePriceDropdownProps) {
  const [open, setOpen] = useState(false)
  const formContext = useUnifiedOrderFormContext()
  const [{ selectedOptions }, setSelectionAtom] = useAtom(
    orderFormSelectionAtomFamily(formContext.cardId)
  )
  const setSelectedOptions = (
    update: SelectedOption[] | ((prev: SelectedOption[]) => SelectedOption[])
  ) =>
    setSelectionAtom((prev) => ({
      ...prev,
      selectedOptions: typeof update === 'function' ? update(prev.selectedOptions) : update,
    }))
  const cardTradeVenue = useOrderFormTradeVenue(formContext.cardId)
  const primaryExchange = useAccountPrimaryExchange(cardTradeVenue)
  // Resolve against the option's OWN exchange, not the card's primary one —
  // positions (and thus shift-clicked options) can come from any connected
  // account/exchange, and scoping to primaryExchange alone silently misses
  // the instrument (empty Strike/Expiry) whenever they differ.
  const multiExchangeInstrumentMap = useMultiExchangeInstrumentMap()
  const optionExchange = parseExchangeField(option.exchange, primaryExchange)
  const instrumentMap = useMemo(
    () => getInstrumentMapForExchange(multiExchangeInstrumentMap, optionExchange),
    [multiExchangeInstrumentMap, optionExchange]
  )
  const instruments = useMemo(() => Array.from(instrumentMap.values()), [instrumentMap])

  // Scroll to selected item on open
  useEffect(() => {
    if (open) {
      // Small timeout to ensure the content is rendered
      const timer = setTimeout(() => {
        const activeItem = document.querySelector(
          '[data-radix-select-viewport] [data-state="checked"]'
        )
        if (activeItem) {
          activeItem.scrollIntoView({ block: 'center' })
        }
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [open])

  const parsed = resolveOptionInstrument(
    option.instrumentName,
    instrumentMap.get(option.instrumentName)
  )
  if (!parsed) return null

  const { ticker, expiry, strike: currentStrike, type } = parsed

  // Get all available strikes for this ticker and expiry
  const availableStrikes = getAvailableStrikes(instruments, ticker, expiry)

  const handleStrikeChange = (newStrikeStr: string) => {
    const newStrike = parseFloat(newStrikeStr)
    if (newStrike === currentStrike) return

    // Build new instrument name with new strike
    // 1. Try to find the exact symbol from global instruments list to preserve format (e.g. '0d275' vs '0.275')
    const matchingInstrument = instruments.find((inst) => {
      const p = resolveOptionInstrument(inst.symbol, inst)
      return (
        p && p.ticker === ticker && p.expiry === expiry && p.strike === newStrike && p.type === type
      )
    })

    const newInstrumentName =
      matchingInstrument?.symbol || buildOptionInstrument(ticker, expiry, newStrike, type)

    // Update selectedOptionsAtom
    setSelectedOptions((prev) =>
      prev.map((opt, i) =>
        i === index
          ? {
              ...opt,
              instrumentName: newInstrumentName,
              price: '-',
            }
          : opt
      )
    )
  }

  const formatStrike = (strike: number) => formatOptionStrike(strike, ticker)

  return (
    <Select
      value={currentStrike.toString()}
      onValueChange={handleStrikeChange}
      onOpenChange={setOpen}
    >
      <SelectTrigger
        hideIcon
        data-1p-ignore
        tabIndex={-1}
        className="font-ibm-plex-sans z-40! h-auto w-fit cursor-pointer gap-1 border-none bg-transparent p-0 text-xs font-medium text-white hover:text-white/80"
      >
        <SelectValue>{formatStrike(currentStrike)}</SelectValue>
      </SelectTrigger>
      <SelectContent className="z-[60] max-h-60">
        {availableStrikes.map((strike) => (
          <SelectItem key={strike} value={strike.toString()} className="text-sm">
            {formatStrike(strike)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/**
 * A strike as the order row shows it: "$70k" for BTC's big round strikes, otherwise "$2,500"
 * (up to 6 decimals under 1, 2 for a fractional strike). Shared with OrderRow's read-only mode.
 */
export function formatOptionStrike(strike: number, ticker: string): string {
  if (ticker.toUpperCase().startsWith('BTC') && strike >= 1000) {
    const kValue = strike / 1000
    const formatted =
      kValue % 1 === 0 ? kValue.toString() : parseFloat(kValue.toFixed(2)).toString()
    return `$${formatted}k`
  }
  const decimals = strike < 1 ? 6 : strike % 1 === 0 ? 0 : 2
  return `$${strike.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  })}`
}
